import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../utils/config';

export const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  // In case a component is rendered outside of AuthProvider (e.g. during HMR),
  // fall back to a safe default instead of throwing and breaking the app.
  if (!context) {
    return {
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      login: async () => ({ success: false }),
      logout: () => {},
      updateUser: () => {}
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const idleTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

  // Setup idle timeout
  const resetIdleTimer = () => {
    lastActivityRef.current = Date.now();
    
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }

    idleTimeoutRef.current = setTimeout(() => {
      handleAutoLogout('Session expired due to inactivity');
    }, IDLE_TIMEOUT);
  };

  // Track user activity
  const handleUserActivity = () => {
    if (isAuthenticated) {
      resetIdleTimer();
    }
  };

  useEffect(() => {
    // Add event listeners for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [isAuthenticated]);

  // Setup axios interceptor for token expiration
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if token is expired or invalid
        if (error.response?.status === 401) {
          const errorData = error.response.data;
          
          // Check for token expiration or invalid token
          if (
            errorData?.code === 'token_not_valid' ||
            errorData?.detail === 'Given token not valid for any token type' ||
            (errorData?.messages && errorData.messages.some(msg => 
              msg.message === 'Token is expired' || 
              msg.token_class === 'AccessToken'
            ))
          ) {
            handleAutoLogout('Your session has expired. Please login again.');
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const handleAutoLogout = (message) => {
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Clear axios authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Update state
    setUser(null);
    setIsAuthenticated(false);
    
    // Clear idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
    
    // Redirect to login with message
    window.location.href = '/login';
    
    // Show message if provided
    if (message) {
      // Authentication warning handled silently
    }
  };

  useEffect(() => {
    // Check if user is already logged in (e.g., from localStorage)
    const checkAuthStatus = () => {
      try {
        const storedUser = localStorage.getItem('user');
        const storedToken = localStorage.getItem('token');
        
        if (storedUser && storedToken) {
          setUser(JSON.parse(storedUser));
          setToken(storedToken);
          setIsAuthenticated(true);
          // Ensure axios has Authorization header on refresh
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          // Start idle timer
          resetIdleTimer();
        }
      } catch (error) {
        // Error checking auth status handled silently
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(API_ENDPOINTS.LOGIN, {
        username_or_email: email,
        password
      }, {
        headers: { 'Content-Type': 'application/json' }
      });

      const token = data?.token || data?.accessToken || data?.access || data?.jwt;
      if (!token) {
        throw new Error('Login succeeded but no token was returned');
      }

      const resolvedUser = data?.user || data?.profile || data?.data?.user || { email };

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(resolvedUser));

      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      setUser(resolvedUser);
      setToken(token);
      setIsAuthenticated(true);

      // Start idle timer after successful login
      resetIdleTimer();

      return { success: true, user: resolvedUser, token };
    } catch (error) {
      const apiMessage = error?.response?.data?.message || error?.response?.data?.detail || error?.response?.data?.error;
      
      // If no specific API message, provide user-friendly error based on status code
      if (!apiMessage) {
        const statusCode = error?.response?.status;
        if (statusCode === 400 || statusCode === 401) {
          throw new Error('Invalid credentials');
        } else if (statusCode === 404) {
          throw new Error('Login service not found');
        } else if (statusCode >= 500) {
          throw new Error('Server error. Please try again later');
        }
      }
      
      throw new Error(apiMessage || 'Login failed. Please try again');
    }
  };

  const logout = () => {
    // Clear localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    
    // Clear axios authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    
    // Clear idle timeout
    if (idleTimeoutRef.current) {
      clearTimeout(idleTimeoutRef.current);
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 